import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { ErrorBanner, PortalPage, TableCard, formatPortalDateTime, statusPillClass } from './ui';

type PortalTimelineResponse = {
  data?: Array<{
    id: string;
    type: string;
    title: string;
    at: string;
    detail: string;
    status: string;
  }>;
};

export default function HistoryPage() {
  const { t } = useTranslation();
  const { fetchJson } = useApiClient();
  const { buildPortalPath, refreshNonce } = usePortalPatientData();
  const [rows, setRows] = useState<PortalTimelineResponse['data']>([]);
  const [error, setError] = useState<string | null>(null);

  const loadTimeline = useCallback(async () => {
    try {
      setError(null);
      const response = await fetchJson<PortalTimelineResponse>(buildPortalPath('/portal/timeline'));
      setRows(Array.isArray(response?.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.message || t('history.loadError'));
    }
  }, [buildPortalPath, fetchJson, t]);

  useEffect(() => {
    void loadTimeline();
  }, [loadTimeline, refreshNonce]);

  return (
    <PortalPage title={t('history.title')} description={t('history.description')}>
      {error ? <ErrorBanner message={error} /> : null}
      <TableCard
        headers={[t('history.headerType'), t('history.headerTitle'), t('history.headerDateTime'), t('history.headerDetail'), t('history.headerStatus')]}
        rows={(rows || []).map((item) => [
          <span key={`${item.id}-type`} className="font-medium text-slate-700">
            {item.type}
          </span>,
          item.title,
          formatPortalDateTime(item.at),
          <span key={`${item.id}-detail`} className="text-slate-500">
            {item.detail}
          </span>,
          <span key={`${item.id}-status`} className={`pill ${statusPillClass(item.status)}`}>
            {item.status}
          </span>,
        ])}
        emptyMessage={t('history.empty')}
      />
    </PortalPage>
  );
}
